import React, { useCallback, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Text } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import Carousel from '../../../../../common/components/Carousel/Carousel';
import CarouselItem from '../../../../../common/components/Carousel/CarouselItem';
import { useContainerMeasurement } from './hooks/useContainerMeasurement';
import { UseCase } from './utils/constants';

interface UseCaseSelectorProps {
  useCases: UseCase[];
  selectedUseCase: string | null;
  onUseCaseSelect: (id: string) => void;
}

const UseCaseSelector: React.FC<UseCaseSelectorProps> = ({
  useCases,
  selectedUseCase,
  onUseCaseSelect,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const handleUseCaseClick = (useCase: UseCase) => {
    onUseCaseSelect(useCase.id);
  };

  const handleKeyDown = (event: React.KeyboardEvent, useCase: UseCase) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleUseCaseClick(useCase);
    }
  };
  const { containerWidth } = useContainerMeasurement(containerRef);

  const getCarouselItemsByContainerWidth = useCallback(
    (containerWidth: number) => {
      if (containerWidth < 500) {
        return 1;
      } else if (containerWidth <= 824) {
        return 3;
      } else {
        return 5;
      }
    },
    []
  );

  return (
    <div className="flex flex-col gap-2 w-full mb-5">
      <Text fontSize="sm" className="text-white m-0">
        Select an edit style:
      </Text>
      <div className="relative" ref={containerRef}>
        <Carousel
          numberItemsToShow={getCarouselItemsByContainerWidth(containerWidth)}
          sliderButtonBackClassName="-left-3 w-7 h-7"
          sliderButtonNextClassName="-right-3 w-7 h-7"
        >
          {useCases.map((useCase) => (
            <CarouselItem key={useCase.id}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => handleUseCaseClick(useCase)}
                onKeyDown={(e) => handleKeyDown(e, useCase)}
                aria-label={`Select ${useCase.title}`}
                className={twMerge(
                  'relative w-full overflow-hidden rounded-xl cursor-pointer transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-opacity-50 border-2',
                  selectedUseCase === useCase.id && 'border-yellow-400'
                )}
                style={{
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                  // These mapto the width/height properties for the element.
                  aspectRatio: '148 / 88',
                  maxWidth: '148px',
                }}
              >
                <img
                  src={`${__ASSETS_COMMON_IMAGES_URL__}/${useCase.thumbnail}`}
                  alt={useCase.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <span className="absolute bottom-3 left-3 text-white text-xs font-semibold drop-shadow-level1-soft">
                  {useCase.title}
                </span>
              </div>
            </CarouselItem>
          ))}
        </Carousel>
      </div>
    </div>
  );
};

export default UseCaseSelector;
